// eslint-disable-next-line import/no-unresolved
import { type Tizen } from 'tizen-common-web'
import { type Webapis } from 'tizen-tv-webapis'

import { getActiveRegionId } from '@sportworld/js-sdk/src/actions/region'
import {
  eventAction,
  eventObject,
} from '@sportworld/js-sdk/src/constants/analytics'
import { isDevMode } from '@sportworld/js-sdk/src/envs/envs'

import { eventCategory } from '@/constants/analytics'
import { tizenKeys, tizenKeyNames } from '@/constants/keys'
import i18n from '@/i18n'
import { ChannelType, DeepLinkType } from '@/types/servicesTypes/configTypes'
import { HasPromotionType, TizenChannel } from '@/types/utilsTypes'
import { sendAnalyticsEvent } from '@/utils/analyticsUtils'
import {
  showError,
  showInfo,
  findChannelByNames,
  findChannelByLocator,
  sendEventToGTAG,
} from '@/utils/utils'

const tizen = (window as { [key: string]: any })['tizen'] as Tizen
const webapis = (window as { [key: string]: any })['webapis'] as Webapis
export default class TizenPlatform {
  name: string
  appBundle: string
  keys: { [key: string]: number }
  duid: string
  uuid: string | undefined
  tifa: string
  lat: boolean | undefined
  version: string
  modelCode: string
  modelName: string
  modelYear: string
  totalMemory: string
  widgetVersion: string | undefined
  constructor() {
    this.name = 'Tizen'
    this.appBundle = '3201903018105'
    this.keys = tizenKeys
    this.duid = ''
    this.uuid = ''
    this.tifa = ''
    this.lat = undefined
    this.modelCode = ''
    this.modelName = ''
    this.modelYear = ''
    this.version = '0'
    this.totalMemory = '0'
    this.widgetVersion = undefined
    this.init()
  }
  init() {
    isDevMode && console.log('[platform] init', this.name)
    this.registerTizenKeys()
    this.setDuid()
    this.setUuid()
    this.setTIFA()
    this.setModelCode()
    this.setModelName()
    this.setModelYear()
    this.setVersion()
    this.setWidgetVersion()
    this.setTotalMemory()
  }
  registerTizenKeys() {
    tizen?.tvinputdevice.registerKeyBatch(
      tizenKeyNames,
      () => {},
      () => {},
    )
  }
  setDuid() {
    let duid: string
    try {
      duid = webapis?.productinfo?.getDuid()
    } catch (e) {
      duid = tizen?.systeminfo?.getCapability('http://tizen.org/system/tizenid')
    }
    this.duid = duid
  }
  setTIFA() {
    let tifa: string
    let lat: boolean | undefined
    try {
      tifa = webapis?.adinfo?.getTIFA()
    } catch (e) {
      tifa = ''
    }
    try {
      lat = webapis?.adinfo?.isLATEnabled()
    } catch (e) {
      lat = false
    }
    this.tifa = tifa
    this.lat = lat
  }
  setUuid() {
    let uuid: string | undefined
    try {
      /**
       * productinfo is missing on webapis type definition
       */
      uuid = (webapis?.productinfo as any)?.getUuid()
    } catch (e) {
      uuid = undefined

      isDevMode && console.log('getUuid is not available on this platform')
    }
    this.uuid = uuid
  }
  setModelCode() {
    let code: string
    try {
      code = webapis?.productinfo?.getModelCode()
    } catch (error) {
      code = 'EMULATOR'
    }
    this.modelCode = code
  }
  setModelName() {
    const name = webapis?.productinfo?.getRealModel()
    this.modelName = name
  }
  setVersion() {
    const userAgent = window.navigator.userAgent
    const pattern = /Tizen\s+(\d+\.\d+)/
    const match = userAgent.match(pattern)
    if (match) {
      this.version = match[1]
    }
  }
  setWidgetVersion() {
    if (!tizen) {
      return
    }
    try {
      const appInfo = tizen.application.getAppInfo()
      const version = appInfo.version
      this.widgetVersion = version
      isDevMode && console.log('Widget version:', version)
    } catch (error) {
      console.warn('Error getting widget version:', error)
    }
  }
  setModelYear() {
    const modelCode = this.getModelCode()
    const year = `20${modelCode?.slice(0, 2)}`
    this.modelYear = year
  }
  getDuid() {
    return this.duid
  }
  getUuid() {
    return this.uuid
  }
  getIfa() {
    return this.tifa
  }
  getLat() {
    return this.lat
  }
  getVersion() {
    return this.version
  }
  getWidgetVersion() {
    return this.widgetVersion
  }
  getModelCode() {
    return this.modelCode
  }
  getModelName() {
    return this.modelName
  }
  getModelYear() {
    return this.modelYear
  }
  getAppBundleName() {
    return this.appBundle
  }

  getTVChannel(channel: ChannelType): Promise<TizenChannel | undefined> {
    return new Promise((resolve, reject) => {
      try {
        /**
         * @example tizen.tvchannel.getChannelList(SuccessCallback, ErrorCallback, TizenChannelType)
         */
        ;(tizen as any)?.tvchannel?.getChannelList(
          (tizenChannelList: TizenChannel[]) => {
            const channelsListData = tizenChannelList

            const channels = channelsListData.map((channel) => ({
              ...channel,
              locator: `dvb://${channel.originalNetworkID}.${channel.transportStreamID}.${channel.programNumber}`,
            }))

            if (channels.length) {
              const channelByName = findChannelByNames(channels, channel)
              if (!channelByName) {
                const channelByLocator = findChannelByLocator(channels, channel)
                resolve(channelByLocator)
              } else {
                resolve(channelByName)
              }
            } else {
              resolve(undefined)
            }
          },
          (error: unknown) => {
            reject(error)
          },
          'ALL',
        )
      } catch (error) {
        reject(error)
      }
    })
  }

  /**
   * @deprecated
   * @description The TV Channel API is not available on Tizen from mid 2024
   */
  isChannelTunable(channel: ChannelType) {
    // const res = Boolean(this.getTVChannel(channel))
    const res = false
    isDevMode && console.log('[tizen][tune] isChannelTunable', res, { channel })
    return res
  }

  async tuneToChannel(channel: ChannelType) {
    const channelName = channel?.name
    const locators = channel?.locators?.join(', ')
    const knownNames = channel?.knownNames?.join(', ')
    const region = getActiveRegionId()

    const channelAnalyticsData = {
      id: channel.id,
      name: channelName,
      platform: this.name,
      region,
    }

    const tuneCB = {
      onsuccess: function () {
        if (isDevMode) {
          showInfo('Tuning success ' + channelName)
          setTimeout(() => {
            try {
              tizen.application.getCurrentApplication().hide()
            } catch (error) {
              showError('App could not be closed: ' + error)
            }
          }, 2000)
        } else {
          try {
            tizen.application.getCurrentApplication().hide()
          } catch (error) {
            showError('App could not be closed: ' + error)
          }
        }
      },
      onprograminforeceived: function () {
        isDevMode && showInfo('Program info received')
      },
      onnosignal: function () {
        isDevMode && showError('No signal')
      },
    }

    const channelCB = (tizenChannel: TizenChannel) => {
      const locator = tizenChannel.locator
      const foundChannelAnalyticsData = {
        ...channelAnalyticsData,
        locator,
      }
      try {
        const major = tizenChannel.major

        try {
          /**
           * tvchannel is missing on tizen type definition
           */
          ;(tizen as any).tvchannel.tune({ major }, tuneCB)
        } catch (error) {
          isDevMode && console.log('[tizen][tune] tune api call error', error)
        }

        sendEventToGTAG(eventCategory.TUNE_CHANNEL, {
          ...foundChannelAnalyticsData,
          status: 'success',
        })
      } catch (error) {
        const e = {
          ...foundChannelAnalyticsData,
          status: 'error',
        }
        sendAnalyticsEvent({
          object: eventObject.VIDEO,
          action: eventAction.TUNING,
          channel: e,
        })
        sendEventToGTAG(eventCategory.TUNE_CHANNEL, e)
      }
    }

    const errorCB = () => {
      showError(i18n.t('tune.noChannel'))

      const e = {
        ...channelAnalyticsData,
        status: 'not found',
      }
      sendAnalyticsEvent({
        object: eventObject.VIDEO,
        action: eventAction.TUNING,
        channel: e,
      })
      sendEventToGTAG(eventCategory.TUNE_CHANNEL, e)
    }

    if (tizen) {
      const channelToTune = await this.getTVChannel(channel)
      channelToTune ? channelCB(channelToTune) : errorCB()
    } else {
      console.error('Tuning requires Tizen TV API.')
      const e = {
        ...channelAnalyticsData,
        locators,
        knownNames,
        status: 'error',
        message: 'Cannot tune to Live TV channel',
      }
      showError(i18n.t('tune.cantTune'))
      sendAnalyticsEvent({
        object: eventObject.VIDEO,
        action: eventAction.TUNING,
        channel: e,
      })
      sendEventToGTAG(eventCategory.TUNE_CHANNEL, e)
    }
  }
  deepLinkToPromotion(promotionData: HasPromotionType) {
    const deepLinkData = {
      values: `{"promotionId": ${promotionData?.promotionInfo?.promotionId}}`,
    }

    const appControlData = new tizen.ApplicationControlData('PAYLOAD', [
      JSON.stringify(deepLinkData),
    ])

    const appControl = new tizen.ApplicationControl(
      'http://tizen.org/appcontrol/operation/view',
      null,
      'image/jpeg',
      null,
      [appControlData],
    )
    const promotionAppID = promotionData?.promotionAppID
    tizen?.application.launchAppControl(
      appControl,
      promotionAppID,
      () => {
        sendEventToGTAG(eventCategory.PROMOTIONS_APP, {
          status: 'success',
        })
      },
      (e: any) => {
        console.error(`Launch PromotionApp Failed.`)
        showError(i18n.t('promotionLaunchFailed', { promotionAppID }))

        console.error(e)

        sendEventToGTAG(eventCategory.PROMOTIONS_APP, {
          status: 'error',
        })
      },
    )
  }
  deepLinkToAppId({
    appId,
    appName = '',
    payload,
  }: Exclude<DeepLinkType['tizen'], undefined>) {
    new Promise<void>((resolve, reject) => {
      const deepLinkData = {
        values: payload,
      }
      try {
        const appControlData = new tizen.ApplicationControlData('PAYLOAD', [
          JSON.stringify(deepLinkData),
        ])

        const appControl = new tizen.ApplicationControl(
          'http://tizen.org/appcontrol/operation/view',
          null,
          'image/jpeg',
          null,
          [appControlData],
        )

        tizen?.application.launchAppControl(
          appControl,
          appId,
          () => {
            isDevMode && console.log(`Launch ${appId} Success`)
            setTimeout(resolve, 10000)
          },
          (e: any) => {
            reject(e)

            console.error(` Launch ${appId} Failed`)
            showError(i18n.t('appNotFound', { appName }))

            console.error(e)
          },
        )
      } catch (e) {
        reject(e)
      }
    })
  }
  setTotalMemory() {
    this.totalMemory = Math.round(
      tizen?.systeminfo?.getTotalMemory() / 1024 / 1024,
    ).toString()
  }
  getTotalMemory() {
    return this.totalMemory
  }
  getAvailableMemory() {
    return Math.round(
      tizen?.systeminfo?.getAvailableMemory() / 1024 / 1024,
    ).toString()
  }
  reloadApp() {
    const appId = tizen?.application?.getAppInfo()?.id
    if (appId) {
      showInfo(`Wait for the reload ${appId}`)
    }
    try {
      if (!tizen || !appId) {
        showInfo('Platform reloadApp not supported')
        return
      }
      const alarmDelaySeconds = 1
      const alarm = new tizen.AlarmRelative(alarmDelaySeconds)
      tizen.alarm.add(alarm, appId)
      this.closeApp()
    } catch (e) {
      showError(`Reload ${appId} Failed`)
    }
  }
  closeApp() {
    isDevMode && console.log('[tizen] exit')
    sendEventToGTAG(eventCategory.APP_CLOSED)
    tizen?.application?.getCurrentApplication().exit()
  }
  openAppStorePage() {
    const sportworldAppId = tizen?.application?.getAppInfo()?.id
    const storeAppId = 'com.samsung.tv.store'
    const appName = 'Samsung TV Store'
    const payload = {
      app_id: sportworldAppId || 'Y9sxevvNCT.SportWorld',
    }
    new Promise<void>((resolve, reject) => {
      const deepLinkData = {
        values: payload,
      }
      try {
        const appControlData = new tizen.ApplicationControlData('PAYLOAD', [
          JSON.stringify(deepLinkData),
        ])

        const appControl = new tizen.ApplicationControl(
          'http://tizen.org/appcontrol/operation/view',
          null,
          'image/jpeg',
          null,
          [appControlData],
        )

        // appControl.appData.setJSON('data', {
        //   app_id: 'Y9sxevvNCT.SportWorld',
        // })

        tizen?.application.launchAppControl(
          appControl,
          storeAppId,
          () => {
            isDevMode && console.log(`Launch ${appName} Success`)
            resolve()
          },
          (e: Error) => {
            reject(e)

            console.error(` Launch ${appName} Failed`)
            console.error(e)

            this.closeApp()
          },
        )
      } catch (e) {
        reject(e)
      }
    })
  }
  // openAppStorePage() {
  //   console.log('[tizen] openAppStorePage')
  //   const appControl = new tizen.ApplicationControl(
  //     'https://www.samsung.com/global/tv/apps/Y9sxevvNCT.SportWorld',
  //   )
  //   tizen.application.launchAppControl(
  //     appControl,
  //     null,
  //     function () {
  //       console.log('TV App store launched')
  //     },
  //     function (error: Error) {
  //       console.log('Error launching TV app store: ' + error.message)
  //     },
  //   )
  // }
}
